import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  BookOpen,
  Check,
  ShoppingCart,
  Download,
  Eye,
  FileText,
  KeyRound,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { AutoLinkText } from "@/components/ui/auto-link-text";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { ProductCard } from "@/features/sales/storefront/components/ProductCard";
import {
  ProductEngagement,
  ProductRatingStars,
  type ProductRatingSummary,
} from "@/features/sales/storefront/components/ProductEngagement";
import { useCatalog } from "@/features/sales/storefront/catalog/CatalogContext";
import { getProductCategories } from "@/features/sales/storefront/catalog/data";
import type { Product } from "@/features/sales/storefront/catalog/types";
import { useCart } from "@/features/sales/storefront/cart/CartContext";
import { useCurrency } from "@/features/sales/storefront/currency/CurrencyContext";
import { absoluteUrl, SITE_ORIGIN } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

async function loadProductMetadata(productId: string): Promise<Product | null> {
  const baseEndpoint =
    typeof window === "undefined" ? `${SITE_ORIGIN}/api/catalog` : "/api/catalog";
  const endpoint = `${baseEndpoint}?product=${encodeURIComponent(productId)}`;
  try {
    const response = await fetch(endpoint, {
      headers: { Accept: "application/json" },
      signal: AbortSignal.timeout(8_000),
    });
    const data = (await response.json().catch(() => null)) as { products?: Product[] } | null;
    if (!response.ok || !data?.products) return null;
    return (
      data.products.find((product) => product.id === productId || product.slug === productId) ??
      null
    );
  } catch {
    return null;
  }
}

export const Route = createFileRoute("/productos/$productId")({
  loader: ({ params }) => loadProductMetadata(params.productId),
  head: ({ loaderData: product, params }) => {
    const title = product ? `${product.title} | Socrattica` : "Libro digital | Socrattica";
    const description =
      product?.subtitle || product?.description || "Conoce este recurso digital de Socrattica.";
    const canonical = absoluteUrl(`/productos/${product?.slug ?? params.productId}`);
    const image = product?.coverImage ? absoluteUrl(product.coverImage) : undefined;
    const productSchema = product
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.title,
          description,
          image: image ? [image] : undefined,
          sku: product.id,
          brand: { "@type": "Brand", name: "Socrattica" },
          author: product.author ? { "@type": "Person", name: product.author } : undefined,
          category: getProductCategories(product).join(", "),
          offers: {
            "@type": "Offer",
            url: canonical,
            price: product.price,
            priceCurrency: product.currency,
            availability: "https://schema.org/InStock",
          },
        }
      : null;
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Inicio",
          item: absoluteUrl("/"),
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Biblioteca",
          item: absoluteUrl("/productos/catalogo"),
        },
        {
          "@type": "ListItem",
          position: 3,
          name: product?.title ?? "Libro digital",
          item: canonical,
        },
      ],
    };

    return {
      meta: [
        { title },
        { name: "description", content: description.slice(0, 160) },
        { property: "og:title", content: title },
        { property: "og:description", content: description.slice(0, 200) },
        { property: "og:type", content: "product" },
        { property: "og:url", content: canonical },
        ...(image ? [{ property: "og:image", content: image }] : []),
        ...(product
          ? [
              { property: "product:price:amount", content: String(product.price) },
              { property: "product:price:currency", content: product.currency },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        ...(productSchema
          ? [{ type: "application/ld+json", children: JSON.stringify(productSchema) }]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbSchema),
        },
      ],
    };
  },
  component: ProductPage,
});

function ProductPage() {
  const { productId } = useParams({ from: "/productos/$productId" });
  const loadedProduct = Route.useLoaderData();
  const { products, getProduct, isLoading } = useCatalog();
  const product = getProduct(productId) ?? loadedProduct;
  const { add } = useCart();
  const { currency, formatPrice } = useCurrency();
  const [ratingSummary, setRatingSummary] = useState<ProductRatingSummary>();

  useEffect(() => {
    if (!product) return;
    setRatingSummary(undefined);
    void trackEvent("view_item", {
      currency: product.currency,
      value: product.price,
      item_id: product.id,
      item_name: product.title,
      items: [
        {
          item_id: product.id,
          item_name: product.title,
          price: product.price,
          quantity: 1,
        },
      ],
    });
  }, [product]);

  if (isLoading && !product) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main id="main-content" className="flex flex-1 items-center justify-center">
          <LoaderCircle className="h-6 w-6 animate-spin text-black" />
          <span className="sr-only">Cargando libro</span>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main
          id="main-content"
          className="flex flex-1 items-center justify-center py-24 text-center"
        >
          <div>
            <h1 className="text-2xl font-semibold tracking-[-0.04em]">Libro no encontrado</h1>
            <p className="mt-2 text-sm text-black/50">El libro que buscas no está disponible.</p>
            <Button asChild className="mt-6">
              <Link to="/productos/catalogo">Volver a la librería</Link>
            </Button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const productCategories = getProductCategories(product);
  const related = products
    .filter(
      (candidate) =>
        candidate.id !== product.id &&
        getProductCategories(candidate).some((category) => productCategories.includes(category)),
    )
    .slice(0, 4);

  const buyNow = () => {
    add(product.id);
    void trackEvent("add_to_cart", {
      currency: product.currency,
      value: product.price,
      items: [
        {
          item_id: product.id,
          item_name: product.title,
          price: product.price,
          quantity: 1,
        },
      ],
    });
    void trackEvent("begin_checkout", {
      currency: product.currency,
      value: product.price,
      item_id: product.id,
      item_name: product.title,
      items: [
        {
          item_id: product.id,
          item_name: product.title,
          price: product.price,
          quantity: 1,
        },
      ],
    });
    toast.success("Añadido al carrito");
    window.location.href = "/productos/carrito";
  };

  return (
    <div className="flex min-h-screen flex-col bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="flex-1">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
          <Link
            to="/productos/catalogo"
            className="inline-flex items-center text-xs text-black/45 transition-colors hover:text-black"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Volver a la librería
          </Link>
        </div>

        <section className="mx-auto grid max-w-7xl gap-10 px-4 pb-16 sm:px-6 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-16">
          <div className="self-start md:sticky md:top-24">
            <div className="flex aspect-[4/5] flex-col items-center justify-center overflow-hidden rounded-xl border border-black/15 bg-black p-8 text-center text-white">
              {product.coverImage ? (
                <img
                  src={product.coverImage}
                  alt={product.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <>
                  <BookOpen className="h-14 w-14 opacity-80 mb-4" />
                  <div className="text-xl font-semibold leading-tight tracking-[-0.04em]">
                    {product.title}
                  </div>
                  {product.level && (
                    <div className="mt-3 text-xs uppercase tracking-widest opacity-80">
                      Nivel {product.level}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
              {product.level ? `Nivel ${product.level} · ` : ""}
              {productCategories.map((category) => category.replace(/-/g, " ")).join(" · ")}
            </p>

            <h1 className="text-4xl font-semibold leading-tight tracking-[-0.05em] md:text-5xl">
              {product.title}
            </h1>
            {product.subtitle && (
              <p className="mt-3 text-sm leading-6 text-black/55">{product.subtitle}</p>
            )}
            {product.author && <p className="mt-2 text-xs text-black/45">Por {product.author}</p>}

            <div className="mt-6 flex items-baseline gap-3">
              <span className="text-3xl font-semibold">
                {formatPrice(product.price, product.currency)}
              </span>
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="text-sm text-muted-foreground line-through">
                  {formatPrice(product.originalPrice, product.currency)}
                </span>
              )}
            </div>
            {ratingSummary && ratingSummary.reviewCount > 0 && (
              <div className="mt-3 flex items-center gap-2 text-sm">
                <ProductRatingStars
                  rating={ratingSummary.averageRating}
                  label={`${ratingSummary.averageRating.toLocaleString("es-CO", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })} de 5 estrellas`}
                />
                <strong>
                  {ratingSummary.averageRating.toLocaleString("es-CO", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                </strong>
                <span className="text-black/45">
                  · {ratingSummary.reviewCount} reseña
                  {ratingSummary.reviewCount === 1 ? "" : "s"}
                </span>
              </div>
            )}
            {currency === "USD" && (
              <p className="mt-2 text-xs text-black/60">
                Conversión estimada. El pago se procesa en COP.
              </p>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <Button size="lg" onClick={buyNow}>
                Comprar ahora
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  add(product.id);
                  void trackEvent("add_to_cart", {
                    currency: product.currency,
                    value: product.price,
                    items: [
                      {
                        item_id: product.id,
                        item_name: product.title,
                        price: product.price,
                        quantity: 1,
                      },
                    ],
                  });
                  toast.success("Añadido al carrito");
                }}
              >
                <ShoppingCart className="h-4 w-4 mr-1.5" /> Añadir al carrito
              </Button>
              {product.sampleUrl && (
                <Button asChild size="lg" variant="outline">
                  <a
                    href={product.sampleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      void trackEvent("sample_open", {
                        item_id: product.id,
                        item_name: product.title,
                      })
                    }
                  >
                    <Eye className="h-4 w-4" />
                    {product.sampleLabel || "Ver muestra PDF"}
                  </a>
                </Button>
              )}
            </div>
            <p className="mt-4 max-w-xl text-xs leading-5 text-black/60">
              Antes de comprar revisa los{" "}
              <Link
                to="/legal/terminos"
                className="font-medium text-black underline underline-offset-2"
              >
                términos de compra
              </Link>
              , la{" "}
              <Link
                to="/legal/reembolsos"
                className="font-medium text-black underline underline-offset-2"
              >
                política de reembolsos
              </Link>{" "}
              y la{" "}
              <Link
                to="/legal/licencia"
                className="font-medium text-black underline underline-offset-2"
              >
                licencia de uso
              </Link>
              .
            </p>

            <div className="mt-8 grid gap-3 text-xs sm:grid-cols-2">
              <div className="flex items-start gap-2 rounded-lg border border-black/15 p-4">
                <Download className="mt-0.5 h-4 w-4" />
                <div>
                  <div className="font-medium">Acceso inmediato</div>
                  <div className="mt-1 text-xs text-black/60">Tras la confirmación del pago.</div>
                </div>
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-black/15 p-4">
                <FileText className="mt-0.5 h-4 w-4" />
                <div>
                  <div className="font-medium">Material digital</div>
                  <div className="mt-1 text-xs text-black/60">
                    {product.format ?? "PDF"}
                    {product.pageCount ? ` · ${product.pageCount} pág.` : ""}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 max-w-none border-t border-black/10 pt-8">
              <h2 className="mb-3 text-xl font-semibold">Sobre este libro</h2>
              <p className="text-sm leading-6 text-black/55">
                <AutoLinkText text={product.description} />
              </p>
            </div>

            {product.included && product.included.length > 0 && (
              <div className="mt-8">
                <h3 className="mb-3 font-semibold">Qué incluye</h3>
                <ul className="space-y-2">
                  {product.included.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.topics && product.topics.length > 0 && (
              <div className="mt-8">
                <h3 className="mb-3 font-semibold">Lo que practicarás</h3>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {product.topics.map((topic) => (
                    <li key={topic} className="flex items-start gap-2 text-sm text-black/70">
                      <Check className="mt-0.5 h-4 w-4 shrink-0" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10 border-t border-black/10 pt-8">
              <h2 className="text-xl font-semibold">Antes de descargar</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <ProductFact
                  icon={<ShieldCheck />}
                  title="Uso personal"
                  description="La compra incluye una licencia personal, no transferible."
                />
                <ProductFact
                  icon={<KeyRound />}
                  title="Acceso privado"
                  description="El recurso queda vinculado a tu cuenta y correo verificado."
                />
                <ProductFact
                  icon={<RefreshCw />}
                  title="Disponible de nuevo"
                  description="Puedes volver a descargarlo desde tu cuenta mientras la compra siga aprobada."
                />
              </div>
            </div>
          </div>
        </section>

        <ProductEngagement product={product} onSummaryChange={setRatingSummary} />

        <section className="border-t border-black/10 py-14">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.65fr_1.35fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
                Preguntas frecuentes
              </p>
              <h2 className="mt-2 text-3xl font-semibold tracking-[-0.05em]">
                Lo esencial, antes de empezar.
              </h2>
            </div>
            <div className="divide-y divide-black/15 border-y border-black/15">
              <ProductFaq
                question="¿Necesito instalar una aplicación?"
                answer={`No. Recibirás un archivo ${product.format ?? "PDF"} que puedes abrir en un computador, tableta o teléfono compatible.`}
              />
              <ProductFaq
                question="¿Dónde encuentro el recurso después?"
                answer="Inicia sesión con el correo usado en la compra. En Mi cuenta encontrarás el historial, el recibo y las descargas disponibles."
              />
              <ProductFaq
                question="¿Puedo compartir el archivo?"
                answer="La licencia incluida es personal. Puedes usar el material en tus propios dispositivos, pero no redistribuirlo ni publicarlo."
              />
              <ProductFaq
                question="¿Qué hago si no puedo descargarlo?"
                answer="Abre un ticket desde el centro de soporte. El equipo verá el historial de la conversación y podrá responder dentro de tu cuenta."
              />
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="border-t border-black/10 bg-white py-16">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
              <h2 className="mb-8 text-2xl font-semibold tracking-[-0.04em]">
                También te puede interesar
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {related.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function ProductFact({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border border-black/15 p-4">
      <span className="text-black [&_svg]:h-4 [&_svg]:w-4">{icon}</span>
      <p className="mt-3 text-xs font-semibold">{title}</p>
      <p className="mt-1 text-xs leading-5 text-black/65">{description}</p>
    </div>
  );
}

function ProductFaq({ question, answer }: { question: string; answer: string }) {
  return (
    <details className="group py-5">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold">
        {question}
        <span
          aria-hidden="true"
          className="text-xl font-light transition-transform group-open:rotate-45"
        >
          +
        </span>
      </summary>
      <p className="max-w-2xl pt-3 text-sm leading-6 text-black/65">{answer}</p>
    </details>
  );
}
